"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EvmBaseAdapter = exports.isEvmChainId = exports.evmChainIds = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const hdwallet_core_1 = require("@shapeshiftoss/hdwallet-core");
const types_1 = require("@shapeshiftoss/types");
const multicoin_address_validator_1 = __importDefault(require("multicoin-address-validator"));
const web3_utils_1 = require("web3-utils");
const ErrorHandler_1 = require("../error/ErrorHandler");
const types_2 = require("../types");
const utils_1 = require("../utils");
const bignumber_1 = require("../utils/bignumber");
const utils_2 = require("./utils");
exports.evmChainIds = [types_1.KnownChainIds.EthereumMainnet, types_1.KnownChainIds.AvalancheMainnet];
const isEvmChainId = (maybeEvmChainId) => {
    return exports.evmChainIds.includes(maybeEvmChainId);
};
exports.isEvmChainId = isEvmChainId;
class EvmBaseAdapter {
    constructor(args) {
        this.chainId = args.chainId;
        this.defaultBIP44Params = args.defaultBIP44Params;
        this.supportedChainIds = args.supportedChainIds;
        this.providers = args.providers;
        this.rpcUrl = args.rpcUrl;
        if (!this.supportedChainIds.includes(this.chainId)) {
            throw new Error(`${this.chainId} not supported. (supported: ${this.supportedChainIds})`);
        }
    }
    getChainId() {
        return this.chainId;
    }
    getRpcUrl() {
        return this.rpcUrl;
    }
    buildBIP44Params(params) {
        return { ...this.defaultBIP44Params, ...params };
    }
    getBIP44Params({ accountNumber }) {
        if (accountNumber < 0) {
            throw new Error('accountNumber must be >= 0');
        }
        return { ...this.defaultBIP44Params, accountNumber };
    }
    async buildSendTransaction(tx) {
        var _a, _b, _c, _d, _e, _f;
        try {
            const { to, wallet, bip44Params = this.defaultBIP44Params, sendMax = false } = tx;
            // If there is a mismatch between the current wallet's EVM chain ID and the adapter's chainId?
            // Switch the chain on wallet before building/sending the Tx
            if ((0, hdwallet_core_1.supportsEthSwitchChain)(wallet)) {
                const walletEvmChainId = await ((_b = (_a = wallet).ethGetChainId) === null || _b === void 0 ? void 0 : _b.call(_a));
                const adapterEvmChainId = (0, caip_1.fromChainId)(this.chainId).chainReference;
                if (!(0, bignumber_1.bnOrZero)(walletEvmChainId).isEqualTo(adapterEvmChainId)) {
                    await ((_d = (_c = wallet).ethSwitchChain) === null || _d === void 0 ? void 0 : _d.call(_c, (0, bignumber_1.bnOrZero)(adapterEvmChainId).toNumber()));
                }
            }
            const { erc20ContractAddress, gasPrice, gasLimit, maxFeePerGas, maxPriorityFeePerGas } = tx.chainSpecific;
            if (!tx.to)
                throw new Error(`${this.getName()}ChainAdapter: to is required`);
            if (!tx.value)
                throw new Error(`${this.getName()}ChainAdapter: value is required`);
            const destAddress = erc20ContractAddress !== null && erc20ContractAddress !== void 0 ? erc20ContractAddress : to;
            const from = await this.getAddress({ bip44Params, wallet });
            const account = await this.getAccount(from);
            const isErc20Send = !!erc20ContractAddress;
            if (sendMax) {
                if (isErc20Send) {
                    const erc20Balance = (_f = (_e = account.chainSpecific.tokens) === null || _e === void 0 ? void 0 : _e.find((token) => {
                        return (0, caip_1.fromAssetId)(token.assetId).assetReference === erc20ContractAddress.toLowerCase();
                    })) === null || _f === void 0 ? void 0 : _f.balance;
                    if (!erc20Balance)
                        throw new Error('no balance');
                    tx.value = erc20Balance;
                }
                else {
                    if ((0, bignumber_1.bnOrZero)(account.balance).isZero())
                        throw new Error('no balance');
                    // (The type system guarantees that either maxFeePerGas or gasPrice will be undefined, but not both)
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    const fee = (0, bignumber_1.bnOrZero)((maxFeePerGas !== null && maxFeePerGas !== void 0 ? maxFeePerGas : gasPrice)).times((0, bignumber_1.bnOrZero)(gasLimit));
                    tx.value = (0, bignumber_1.bnOrZero)(account.balance).minus(fee).toString();
                }
            }
            const data = await (0, utils_2.getErc20Data)(to, tx.value, erc20ContractAddress);
            const fees = (() => {
                if (maxFeePerGas && maxPriorityFeePerGas) {
                    return {
                        maxFeePerGas: (0, web3_utils_1.numberToHex)(maxFeePerGas),
                        maxPriorityFeePerGas: (0, web3_utils_1.numberToHex)(maxPriorityFeePerGas),
                    };
                }
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                return { gasPrice: (0, web3_utils_1.numberToHex)(tx.chainSpecific.gasPrice) };
            })();
            const txToSign = {
                addressNList: (0, utils_1.toAddressNList)(bip44Params),
                value: (0, web3_utils_1.numberToHex)(isErc20Send ? '0' : tx.value),
                to: destAddress,
                chainId: Number((0, caip_1.fromChainId)(this.chainId).chainReference),
                data,
                nonce: (0, web3_utils_1.numberToHex)(account.chainSpecific.nonce),
                gasLimit: (0, web3_utils_1.numberToHex)(gasLimit),
                ...fees,
            };
            return { txToSign };
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
    async getAccount(pubkey) {
        try {
            const data = await this.providers.http.getAccount({ pubkey });
            const balance = (0, bignumber_1.bnOrZero)(data.balance).plus((0, bignumber_1.bnOrZero)(data.unconfirmedBalance));
            return {
                balance: balance.toString(),
                chainId: this.chainId,
                assetId: this.assetId,
                chain: this.getType(),
                chainSpecific: {
                    nonce: data.nonce,
                    tokens: data.tokens.map((token) => ({
                        balance: token.balance,
                        assetId: (0, caip_1.toAssetId)({
                            chainId: this.chainId,
                            assetNamespace: (0, utils_1.getAssetNamespace)(token.type),
                            assetReference: token.contract,
                        }),
                    })),
                },
                pubkey: data.pubkey,
            };
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
    async getTxHistory(input) {
        var _a;
        const data = await this.providers.http.getTxHistory({
            pubkey: input.pubkey,
            pageSize: input.pageSize,
            cursor: input.cursor,
        });
        const txs = await Promise.all(data.txs.map(async (tx) => {
            const parsedTx = await this.parser.parse(tx, input.pubkey);
            return {
                address: input.pubkey,
                blockHash: parsedTx.blockHash,
                blockHeight: parsedTx.blockHeight,
                blockTime: parsedTx.blockTime,
                chainId: parsedTx.chainId,
                chain: this.getType(),
                confirmations: parsedTx.confirmations,
                txid: parsedTx.txid,
                fee: parsedTx.fee,
                status: parsedTx.status,
                trade: parsedTx.trade,
                transfers: parsedTx.transfers.map((transfer) => ({
                    assetId: transfer.assetId,
                    from: transfer.from,
                    to: transfer.to,
                    type: transfer.type,
                    value: transfer.totalValue,
                })),
                data: parsedTx.data,
            };
        }));
        return {
            cursor: (_a = data.cursor) !== null && _a !== void 0 ? _a : '',
            pubkey: input.pubkey,
            transactions: txs,
        };
    }
    async signTransaction(signTxInput) {
        try {
            const { txToSign, wallet } = signTxInput;
            const signedTx = await wallet.ethSignTx(txToSign);
            if (!signedTx)
                throw new Error('Error signing tx');
            return signedTx.serialized;
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
    async signAndBroadcastTransaction(signTxInput) {
        var _a;
        try {
            const { txToSign, wallet } = signTxInput;
            const txHash = await ((_a = wallet === null || wallet === void 0 ? void 0 : wallet.ethSendTx) === null || _a === void 0 ? void 0 : _a.call(wallet, txToSign));
            if (!txHash)
                throw new Error('Error signing & broadcasting tx');
            return txHash.hash;
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
    async broadcastTransaction(hex) {
        return this.providers.http.sendTx({ sendTxBody: { hex } });
    }
    async signMessage(signMessageInput) {
        try {
            const { messageToSign, wallet } = signMessageInput;
            const signedMessage = await wallet.ethSignMessage(messageToSign);
            if (!signedMessage)
                throw new Error('EvmBaseAdapter: error signing message');
            return signedMessage.signature;
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
    async getAddress(input) {
        const { wallet, bip44Params = this.defaultBIP44Params, showOnDevice = false } = input;
        const address = await wallet.ethGetAddress({
            addressNList: (0, utils_1.toAddressNList)(bip44Params),
            showDisplay: showOnDevice,
        });
        if (!address)
            throw new Error('EvmBaseAdapter: no address available from wallet');
        return address;
    }
    async validateAddress(address) {
        const chainLabel = (0, utils_1.chainIdToChainLabel)(this.chainId);
        const isValidAddress = multicoin_address_validator_1.default.validate(address, chainLabel);
        if (isValidAddress)
            return { valid: true, result: types_2.ValidAddressResultType.Valid };
        return { valid: false, result: types_2.ValidAddressResultType.Invalid };
    }
    async subscribeTxs(input, onMessage, onError) {
        const { wallet, bip44Params = this.defaultBIP44Params } = input;
        const address = await this.getAddress({ wallet, bip44Params });
        const subscriptionId = (0, utils_1.toRootDerivationPath)(bip44Params);
        await this.providers.ws.subscribeTxs(subscriptionId, { topic: 'txs', addresses: [address] }, async (msg) => {
            const tx = await this.parser.parse(msg.data, msg.address);
            onMessage({
                address: tx.address,
                blockHash: tx.blockHash,
                blockHeight: tx.blockHeight,
                blockTime: tx.blockTime,
                chainId: tx.chainId,
                confirmations: tx.confirmations,
                fee: tx.fee,
                status: tx.status,
                trade: tx.trade,
                transfers: tx.transfers.map((transfer) => ({
                    assetId: transfer.assetId,
                    from: transfer.from,
                    to: transfer.to,
                    type: transfer.type,
                    value: transfer.totalValue,
                })),
                txid: tx.txid,
                data: tx.data,
            });
        }, (err) => onError({ message: err.message }));
    }
    unsubscribeTxs(input) {
        if (!input)
            return this.providers.ws.unsubscribeTxs();
        const { bip44Params = this.defaultBIP44Params } = input;
        const subscriptionId = (0, utils_1.toRootDerivationPath)(bip44Params);
        this.providers.ws.unsubscribeTxs(subscriptionId, { topic: 'txs', addresses: [] });
    }
    closeTxs() {
        this.providers.ws.close('txs');
    }
    async buildCustomTx({ maxFeePerGas, maxPriorityFeePerGas, gasPrice, value, data, gasLimit, to, wallet, bip44Params = this.defaultBIP44Params, }) {
        try {
            const chainReference = (0, caip_1.fromChainId)(this.chainId).chainReference;
            const from = await this.getAddress({ bip44Params, wallet });
            const account = await this.getAccount(from);
            const fees = maxFeePerGas && maxPriorityFeePerGas
                ? {
                    maxFeePerGas: (0, web3_utils_1.numberToHex)(maxFeePerGas),
                    maxPriorityFeePerGas: (0, web3_utils_1.numberToHex)(maxPriorityFeePerGas),
                }
                : { gasPrice: (0, web3_utils_1.numberToHex)(gasPrice !== null && gasPrice !== void 0 ? gasPrice : '0') };
            const txToSign = {
                addressNList: (0, utils_1.toAddressNList)(bip44Params),
                value,
                to,
                chainId: Number(chainReference),
                data,
                nonce: (0, web3_utils_1.numberToHex)(account.chainSpecific.nonce),
                gasLimit: (0, web3_utils_1.numberToHex)(gasLimit),
                ...fees,
            };
            return { txToSign };
        }
        catch (err) {
            return (0, ErrorHandler_1.ErrorHandler)(err);
        }
    }
}
exports.EvmBaseAdapter = EvmBaseAdapter;
